import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserDetail, patchUser, upsertSubscription, deleteSubscription, cancelSubscription } from '../api/userAccounts';
import type { UserAccountDetail, SubscriptionPayload, UserPatchPayload } from '../types/userAccount';
import { PLAN_NAMES } from '../types/userAccount';
import { useAuth } from '../hooks';
import styles from './AccountDetail.module.css';

function fmt(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toInputDatetime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 16);
}

export default function AccountDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { isSupportOnly } = useAuth();

  const [user, setUser] = useState<UserAccountDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit states
  const [editingTrial, setEditingTrial] = useState(false);
  const [trialActive, setTrialActive] = useState(false);
  const [trialExpiry, setTrialExpiry] = useState('');

  const [editingSub, setEditingSub] = useState(false);
  const [subForm, setSubForm] = useState<SubscriptionPayload>({
    stripe_subscription_id: '', plan_name: 'Monthly Standard', status: 'active',
    start_date: '', end_date: '', active: true,
  });

  const [editingStripe, setEditingStripe] = useState(false);
  const [stripeId, setStripeId] = useState('');

  const [saving, setSaving] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUserDetail(parseInt(userId));
      setUser(data);
    } catch {
      setError('Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Populate edit forms when user data loads
  useEffect(() => {
    if (!user) return;
    setTrialActive(user.is_trial_active);
    setTrialExpiry(toInputDatetime(user.free_plan_expiry_date));
    setStripeId(user.stripe_customer_id || '');
    if (user.subscription) {
      setSubForm({
        stripe_subscription_id: user.subscription.stripe_subscription_id,
        plan_name: user.subscription.plan_name,
        status: user.subscription.status,
        start_date: toInputDatetime(user.subscription.start_date),
        end_date: toInputDatetime(user.subscription.end_date),
        active: user.subscription.active,
      });
    } else {
      setSubForm({
        stripe_subscription_id: '', plan_name: 'Monthly Standard', status: 'active',
        start_date: toInputDatetime(new Date().toISOString()), end_date: '', active: true,
      });
    }
  }, [user]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // --- Actions ---

  const handleSaveTrial = async () => {
    if (!user) return;
    setSaving('trial');
    try {
      const payload: UserPatchPayload = { is_trial_active: trialActive };
      if (trialExpiry) payload.free_plan_expiry_date = new Date(trialExpiry).toISOString();
      const updated = await patchUser(user.id, payload);
      setUser(updated);
      setEditingTrial(false);
      showSuccess('Trial settings updated');
    } catch {
      setError('Failed to update trial');
    } finally {
      setSaving(null);
    }
  };

  const handleResetTrial = async () => {
    if (!user) return;
    setSaving('trial');
    try {
      const expiry = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const updated = await patchUser(user.id, {
        is_trial_active: true,
        free_plan_expiry_date: expiry,
      });
      setUser(updated);
      showSuccess('Trial reset — 14 days from now');
    } catch {
      setError('Failed to reset trial');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveSubscription = async () => {
    if (!user) return;
    setSaving('sub');
    try {
      const payload: SubscriptionPayload = {
        ...subForm,
        start_date: new Date(subForm.start_date).toISOString(),
        end_date: subForm.end_date ? new Date(subForm.end_date).toISOString() : undefined,
      };
      await upsertSubscription(user.id, payload);
      await loadUser();
      setEditingSub(false);
      showSuccess(user.subscription ? 'Subscription updated' : 'Subscription created');
    } catch {
      setError('Failed to save subscription');
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteSubscription = async (reactivateTrial: boolean) => {
    if (!user) return;
    setSaving('sub');
    try {
      await deleteSubscription(user.id, reactivateTrial);
      await loadUser();
      setConfirmDelete(false);
      showSuccess('Subscription deleted' + (reactivateTrial ? ' — trial reactivated' : ''));
    } catch {
      setError('Failed to delete subscription');
    } finally {
      setSaving(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user) return;
    setSaving('cancelSub');
    setError(null);
    try {
      const result = await cancelSubscription(user.id);
      await loadUser();
      setConfirmCancel(false);
      showSuccess(result.message);
    } catch (err) {
      // Refusals carry an actionable reason (App Store purchase, already
      // cancelled, Stripe rejection) that a generic message would throw away.
      const detail =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(detail || 'Failed to cancel subscription');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveStripe = async () => {
    if (!user) return;
    setSaving('stripe');
    try {
      const updated = await patchUser(user.id, { stripe_customer_id: stripeId || undefined });
      setUser(updated);
      setEditingStripe(false);
      showSuccess('Stripe Customer ID updated');
    } catch {
      setError('Failed to update Stripe ID');
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async () => {
    if (!user) return;
    setSaving('active');
    try {
      const updated = await patchUser(user.id, { is_active: !user.is_active });
      setUser(updated);
      showSuccess(updated.is_active ? 'Account activated' : 'Account deactivated');
    } catch {
      setError('Failed to update account status');
    } finally {
      setSaving(null);
    }
  };

  if (isLoading) return <div className={styles.page}><div className={styles.loading}>Loading user...</div></div>;
  if (error && !user) return (
    <div className={styles.page}>
      <div className={styles.error}>{error}<button className={styles.retryButton} onClick={loadUser}>Retry</button></div>
    </div>
  );
  if (!user) return null;

  const stripeUrl = user.stripe_customer_id
    ? `https://dashboard.stripe.com/customers/${user.stripe_customer_id}`
    : null;

  // Matches the states the cancel endpoint accepts. Lifetime purchases
  // ('succeeded') have nothing recurring to cancel.
  const canCancelSubscription =
    user.subscription?.status === 'active' || user.subscription?.status === 'past_due';

  return (
    <div className={styles.page}>
      <button className={styles.backButton} onClick={() => navigate('/accounts')}>← Back to Users</button>

      {successMsg && <div className={styles.success}>{successMsg}</div>}
      {error && <div className={styles.error}>{error}<button className={styles.dismissButton} onClick={() => setError(null)}>✕</button></div>}

      {/* Header */}
      <div className={styles.userHeader}>
        <div>
          <h1 className={styles.title}>{user.email}</h1>
          <p className={styles.subtitle}>User #{user.id}</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={loadUser} disabled={isLoading}>
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
          </button>
          {stripeUrl && (
            <a href={stripeUrl} target="_blank" rel="noopener noreferrer" className={styles.stripeLink}>
              Open in Stripe →
            </a>
          )}
        </div>
      </div>

      <div className={styles.sections}>
        {/* Section 1: Account Info */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Account Info</h2>
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Email</span>
              <span className={styles.fieldValue}>{user.email}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Account Status</span>
              <span className={styles.fieldValue}>
                <span className={user.is_active ? styles.activeStatus : styles.inactiveStatus}>
                  {user.is_active ? 'Active' : 'Deactivated'}
                </span>
                {!isSupportOnly && (
                  <button
                    className={user.is_active ? styles.dangerButtonSmall : styles.successButtonSmall}
                    onClick={handleToggleActive}
                    disabled={saving === 'active'}
                  >
                    {saving === 'active' ? '...' : user.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Staff</span>
              <span className={styles.fieldValue}>{user.is_staff ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </section>

        {/* Section 2: Trial Status */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Trial Status</h2>
            <div className={styles.sectionActions}>
              <button className={styles.actionButton} onClick={handleResetTrial} disabled={saving === 'trial'}>
                {saving === 'trial' && !editingTrial ? '...' : 'Reset Trial (14 days)'}
              </button>
              {!editingTrial && (
                <button className={styles.editButton} onClick={() => setEditingTrial(true)}>Edit</button>
              )}
            </div>
          </div>
          {editingTrial ? (
            <div className={styles.editForm}>
              <label className={styles.formLabel}>
                <input
                  type="checkbox"
                  checked={trialActive}
                  onChange={(e) => setTrialActive(e.target.checked)}
                />
                Trial Active
              </label>
              <label className={styles.formLabel}>
                Expiry Date
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={trialExpiry}
                  onChange={(e) => setTrialExpiry(e.target.value)}
                />
              </label>
              <div className={styles.formActions}>
                <button className={styles.saveButton} onClick={handleSaveTrial} disabled={saving === 'trial'}>
                  {saving === 'trial' ? 'Saving...' : 'Save'}
                </button>
                <button className={styles.cancelButton} onClick={() => setEditingTrial(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Trial Active</span>
                <span className={`${styles.fieldValue} ${user.is_trial_active ? styles.activeStatus : styles.inactiveStatus}`}>
                  {user.is_trial_active ? 'Yes' : 'No'}
                </span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Has Used Trial</span>
                <span className={styles.fieldValue}>{user.has_used_trial ? 'Yes' : 'No'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Trial Start</span>
                <span className={styles.fieldValue}>{fmt(user.trial_start_date)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Expiry Date</span>
                <span className={styles.fieldValue}>{fmt(user.free_plan_expiry_date)}</span>
              </div>
            </div>
          )}
        </section>

        {/* Section 3: Subscription */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Subscription</h2>
            <div className={styles.sectionActions}>
              {canCancelSubscription && !editingSub && !confirmDelete && !confirmCancel && (
                <button className={styles.actionButton} onClick={() => setConfirmCancel(true)}>
                  Cancel in Stripe
                </button>
              )}
              {!isSupportOnly && !editingSub && (
                <button className={styles.editButton} onClick={() => setEditingSub(true)}>
                  {user.subscription ? 'Edit' : 'Create Subscription'}
                </button>
              )}
              {!isSupportOnly && user.subscription && !editingSub && !confirmDelete && (
                <button className={styles.dangerButtonSmall} onClick={() => setConfirmDelete(true)}>Delete</button>
              )}
            </div>
          </div>

          {confirmCancel && (
            <div className={styles.confirmBar}>
              <span>
                Cancel this subscription in Stripe? The customer keeps access until the end of
                the period they have paid for, and will not be billed again.{' '}
              </span>
              <button className={styles.dangerButtonSmall} onClick={handleCancelSubscription} disabled={saving === 'cancelSub'}>
                {saving === 'cancelSub' ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
              <button className={styles.cancelButton} onClick={() => setConfirmCancel(false)}>Keep It</button>
            </div>
          )}

          {confirmDelete && (
            <div className={styles.confirmBar}>
              <span>
                Delete the local subscription record? This does <strong>not</strong> stop
                billing in Stripe — use “Cancel in Stripe” for that.{' '}
              </span>
              <button className={styles.dangerButtonSmall} onClick={() => handleDeleteSubscription(false)} disabled={saving === 'sub'}>
                Delete Only
              </button>
              <button className={styles.actionButton} onClick={() => handleDeleteSubscription(true)} disabled={saving === 'sub'}>
                Delete & Reactivate Trial
              </button>
              <button className={styles.cancelButton} onClick={() => setConfirmDelete(false)}>Cancel</button>
            </div>
          )}

          {editingSub ? (
            <div className={styles.editForm}>
              <label className={styles.formLabel}>
                Stripe Subscription / Payment Intent ID
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="sub_xxx or pi_xxx"
                  value={subForm.stripe_subscription_id}
                  onChange={(e) => setSubForm(f => ({ ...f, stripe_subscription_id: e.target.value }))}
                />
              </label>
              <label className={styles.formLabel}>
                Plan Name
                <select
                  className={styles.formSelect}
                  value={subForm.plan_name}
                  onChange={(e) => setSubForm(f => ({ ...f, plan_name: e.target.value as typeof f.plan_name }))}
                >
                  {PLAN_NAMES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={styles.formLabel}>
                Status
                <select
                  className={styles.formSelect}
                  value={subForm.status}
                  onChange={(e) => setSubForm(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="active">active</option>
                  <option value="canceled">canceled</option>
                  <option value="succeeded">succeeded (lifetime)</option>
                  <option value="trialing">trialing</option>
                </select>
              </label>
              <label className={styles.formLabel}>
                Start Date
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={subForm.start_date}
                  onChange={(e) => setSubForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </label>
              <label className={styles.formLabel}>
                End Date
                <input
                  type="datetime-local"
                  className={styles.formInput}
                  value={subForm.end_date || ''}
                  onChange={(e) => setSubForm(f => ({ ...f, end_date: e.target.value }))}
                />
              </label>
              <label className={styles.formLabel}>
                <input
                  type="checkbox"
                  checked={subForm.active}
                  onChange={(e) => setSubForm(f => ({ ...f, active: e.target.checked }))}
                />
                Active
              </label>
              <div className={styles.formActions}>
                <button className={styles.saveButton} onClick={handleSaveSubscription} disabled={saving === 'sub'}>
                  {saving === 'sub' ? 'Saving...' : user.subscription ? 'Update Subscription' : 'Create Subscription'}
                </button>
                <button className={styles.cancelButton} onClick={() => setEditingSub(false)}>Cancel</button>
              </div>
            </div>
          ) : user.subscription ? (
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Plan</span>
                <span className={styles.fieldValue}>{user.subscription.plan_name}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Status</span>
                <span className={`${styles.fieldValue} ${
                  user.subscription.status === 'active' || user.subscription.status === 'succeeded'
                    ? styles.activeStatus : user.subscription.status === 'canceled'
                    ? styles.canceledStatus : ''
                }`}>
                  {user.subscription.status}
                </span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Active</span>
                <span className={styles.fieldValue}>{user.subscription.active ? 'Yes' : 'No'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Stripe ID</span>
                <span className={`${styles.fieldValue} ${styles.mono}`}>{user.subscription.stripe_subscription_id}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Start Date</span>
                <span className={styles.fieldValue}>{fmt(user.subscription.start_date)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>End Date</span>
                <span className={styles.fieldValue}>{fmt(user.subscription.end_date)}</span>
              </div>
              {user.subscription.canceled_at && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Canceled At</span>
                  <span className={styles.fieldValue}>{fmt(user.subscription.canceled_at)}</span>
                </div>
              )}
            </div>
          ) : (
            <p className={styles.emptyNotice}>No subscription — user is on free/trial tier.</p>
          )}
        </section>

        {/* Section 4: Stripe Info */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Stripe Info</h2>
            {!isSupportOnly && !editingStripe && (
              <button className={styles.editButton} onClick={() => setEditingStripe(true)}>Edit</button>
            )}
          </div>
          {editingStripe ? (
            <div className={styles.editForm}>
              <label className={styles.formLabel}>
                Stripe Customer ID
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="cus_xxx"
                  value={stripeId}
                  onChange={(e) => setStripeId(e.target.value)}
                />
              </label>
              <div className={styles.formActions}>
                <button className={styles.saveButton} onClick={handleSaveStripe} disabled={saving === 'stripe'}>
                  {saving === 'stripe' ? 'Saving...' : 'Save'}
                </button>
                <button className={styles.cancelButton} onClick={() => { setEditingStripe(false); setStripeId(user.stripe_customer_id || ''); }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Customer ID (User)</span>
                <span className={`${styles.fieldValue} ${styles.mono}`}>{user.stripe_customer_id || '-'}</span>
              </div>
              {user.stripe_customer && (
                <>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Customer ID (StripeCustomer)</span>
                    <span className={`${styles.fieldValue} ${styles.mono}`}>{user.stripe_customer.stripe_customer_id}</span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>StripeCustomer Created</span>
                    <span className={styles.fieldValue}>{fmt(user.stripe_customer.created_at)}</span>
                  </div>
                </>
              )}
              {stripeUrl && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Stripe Dashboard</span>
                  <a href={stripeUrl} target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                    Open →
                  </a>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Section 5: Payment Methods */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Payment Methods</h2>
          {user.payment_methods.length === 0 ? (
            <p className={styles.emptyNotice}>No payment methods on file.</p>
          ) : (
            <div className={styles.cardList}>
              {user.payment_methods.map(pm => (
                <div key={pm.id} className={styles.paymentCard}>
                  <span className={styles.cardBrand}>{pm.card_brand.toUpperCase()}</span>
                  <span className={styles.cardNumber}>•••• {pm.card_last4}</span>
                  <span className={styles.cardExpiry}>{String(pm.card_exp_month).padStart(2, '0')}/{pm.card_exp_year}</span>
                  <span className={styles.cardDate}>Added {fmtDate(pm.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 6: Payment History */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Payment History</h2>
          {user.payment_transactions.length === 0 ? (
            <p className={styles.emptyNotice}>No payment transactions recorded.</p>
          ) : (
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Payment Intent</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {user.payment_transactions.map(tx => (
                  <tr key={tx.id}>
                    <td className={styles.amountCell}>${tx.amount}</td>
                    <td>
                      <span className={tx.status === 'succeeded' ? styles.statusSuccess : styles.statusFailed}>
                        {tx.status}
                      </span>
                    </td>
                    <td className={styles.mono}>{tx.stripe_payment_intent_id}</td>
                    <td>{fmt(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Section 7: Devices */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Devices ({user.devices.length})</h2>
            {user.devices.length > 0 && (
              <button
                className={styles.actionButton}
                onClick={() => navigate(`/events?user_email=${encodeURIComponent(user.email)}`)}
              >
                View all sync events →
              </button>
            )}
          </div>
          {user.devices.length === 0 ? (
            <p className={styles.emptyNotice}>No devices linked.</p>
          ) : (
            <div className={styles.deviceList}>
              {user.devices.map(dev => (
                <div
                  key={dev.id}
                  className={`${styles.deviceCard} ${styles.deviceCardClickable} ${!dev.linked_at ? styles.devicePending : ''}`}
                  onClick={() => navigate(`/events?device_code=${encodeURIComponent(dev.device_id)}`)}
                >
                  <div className={styles.deviceHeader}>
                    <span className={styles.deviceId}>{dev.device_id}</span>
                    {dev.name && <span className={styles.deviceName}>{dev.name}</span>}
                    {dev.linked_at ? (
                      <span className={styles.deviceConfirmedBadge}>Confirmed</span>
                    ) : (
                      <span className={styles.devicePendingBadge}>Pending</span>
                    )}
                  </div>
                  {!dev.linked_at && (
                    <div className={styles.devicePendingNotice}>
                      Waiting for device to connect — user claimed this device but it hasn't picked up its token yet.
                    </div>
                  )}
                  <div className={styles.deviceMeta}>
                    {dev.firmware_version && <span>FW: {dev.firmware_version}</span>}
                    {dev.pico_version && <span>Pico: {dev.pico_version}</span>}
                    {dev.linked_at && <span>Linked: {fmtDate(dev.linked_at)}</span>}
                    {dev.last_synced_at && <span>Last sync: {fmt(dev.last_synced_at)}</span>}
                  </div>
                  <div className={styles.deviceActions}>
                    <span className={styles.viewActivityLink}>View sync activity →</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Section 8: Discount Codes */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Discount Codes</h2>
          {user.discount_codes_used.length === 0 ? (
            <p className={styles.emptyNotice}>No discount codes used.</p>
          ) : (
            <table className={styles.miniTable}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Used At</th>
                </tr>
              </thead>
              <tbody>
                {user.discount_codes_used.map((dc, i) => (
                  <tr key={i}>
                    <td className={styles.mono}>{dc.code}</td>
                    <td>{dc.discount_percent}%</td>
                    <td>{fmt(dc.used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Section 9: Shopify Orders */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Shopify Orders</h2>
          {!user.shopify_purchases || user.shopify_purchases.length === 0 ? (
            <p className={styles.emptyNotice}>No Shopify orders found for this email.</p>
          ) : (
            <div className={styles.shopifyOrderList}>
              {Object.entries(
                user.shopify_purchases.reduce<Record<string, typeof user.shopify_purchases>>((groups, p) => {
                  (groups[p.shopify_order_id] ||= []).push(p);
                  return groups;
                }, {})
              ).map(([orderId, items]) => (
                <div key={orderId} className={styles.shopifyOrder}>
                  <div className={styles.shopifyOrderHeader}>
                    <span className={styles.shopifyOrderId}>Order #{orderId}</span>
                    <span className={styles.shopifyOrderDate}>{fmtDate(items[0].created_at)}</span>
                  </div>
                  {items[0].customer_name && (
                    <div className={styles.shopifyCustomer}>
                      {items[0].customer_name} ({items[0].customer_email})
                    </div>
                  )}
                  <div className={styles.shopifyItems}>
                    {items.map(item => (
                      <div key={item.id} className={styles.shopifyItem}>
                        <span className={styles.shopifyProductTitle}>{item.product_title}</span>
                        {item.is_subscription_product && (
                          <span className={styles.shopifyBundleBadge}>Bundle</span>
                        )}
                        {item.is_subscription_product && item.discount_code_sent && (
                          <span className={styles.shopifyDiscountSent}>Discount code sent</span>
                        )}
                        {item.is_subscription_product && !item.discount_code_sent && (
                          <span className={styles.shopifyDiscountNotSent}>Discount code NOT sent</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.shopifyLinks}>
                    <a href={items[0].shopify_order_url} target="_blank" rel="noopener noreferrer" className={styles.shopifyLink}>
                      View Order in Shopify →
                    </a>
                    <a href={items[0].shopify_customer_url} target="_blank" rel="noopener noreferrer" className={styles.shopifyLink}>
                      View Customer in Shopify →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
